import React from 'react';
import { Link } from 'react-router-dom';
import { Anuncio } from '../../types';
import { useFavoritos } from '../../hooks/useAnuncios';
import { useAuthStore } from '../../store/authStore';

interface AnuncioCardProps {
  anuncio: Anuncio;
  showFavorite?: boolean;
}

const AnuncioCard: React.FC<AnuncioCardProps> = ({ anuncio, showFavorite = true }) => {
  const { isAuthenticated } = useAuthStore();
  const { isFavorito, toggleFavorito } = useFavoritos();
  const isFav = isFavorito(anuncio.id);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      await toggleFavorito(anuncio.id);
    }
  };

  const statusColors = {
    ativo: 'bg-green-100 text-green-800',
    pausado: 'bg-amber-100 text-amber-800',
    vendido: 'bg-blue-100 text-blue-800',
    expirado: 'bg-gray-100 text-gray-800',
    pendente: 'bg-orange-100 text-orange-800',
  };

  const mainImage = anuncio.imagens?.find((img) => img.principal)?.url || 
                    anuncio.imagens?.[0]?.url || 
                    '/placeholder-animal.jpg';

  return (
    <Link
      to={`/anuncios/${anuncio.id}`}
      className="card group block"
    >
      {/* Image Container */}
      <div className="relative aspect-card overflow-hidden bg-gray-100">
        <img
          src={mainImage}
          alt={anuncio.titulo}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {anuncio.destaque && (
            <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              ⭐ Destaque
            </span>
          )}
          {anuncio.status !== 'ativo' && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColors[anuncio.status]}`}>
              {anuncio.status.charAt(0).toUpperCase() + anuncio.status.slice(1)}
            </span>
          )}
        </div>

        {/* Favorite Button */}
        {showFavorite && isAuthenticated && (
          <button
            onClick={handleFavoriteClick}
            className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              isFav
                ? 'bg-red-500 text-white'
                : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
            }`}
            aria-label={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <svg
              className="w-5 h-5"
              fill={isFav ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        )}

        {/* Image count */}
        {anuncio.imagens && anuncio.imagens.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {anuncio.imagens.length}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Category */}
        <span className="text-xs font-medium text-green-700 uppercase tracking-wide">
          {anuncio.categoria}
        </span>

        {/* Title */}
        <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2 group-hover:text-green-700 transition-colors">
          {anuncio.titulo}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{anuncio.localizacao?.cidade}, {anuncio.localizacao?.estado}</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div>
            <p className="text-xl font-bold text-green-700">
              {formatPrice(anuncio.preco)}
            </p>
            {anuncio.precoNegociavel && (
              <span className="text-xs text-gray-500">Preço negociável</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {anuncio.visualizacoes}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {anuncio.favoritosCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default AnuncioCard;
